import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Link2, RefreshCw, X, AlertTriangle, Plus, ExternalLink, FlaskConical, Trash2, Loader2, Database, Eraser, Music2 } from "lucide-react";
import { useEffect } from "react";
import { useSearch } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { useAuthUser } from "@/App";
import type { Brand } from "@shared/schema";

interface ConnectionData {
  id: number;
  fbUserId: string;
  fbUserName: string | null;
  fbUserEmail: string | null;
  status: string | null;
  tokenExpiresAt: string | null;
  lastSyncAt: string | null;
  errorMessage: string | null;
  createdAt: string | null;
  accounts: Array<{
    id: number;
    accountId: string;
    accountName: string;
    brandId: number | null;
    isActive: boolean | null;
    syncStatus: string | null;
    lastSyncAt: string | null;
    syncErrorMessage: string | null;
    unifiedAccountId: number | null;
  }>;
}

function formatTimeAgo(date: string | null): string {
  if (!date) return "Never";
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function daysUntilExpiry(date: string | null): number | null {
  if (!date) return null;
  return Math.floor((new Date(date).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

const syncStatusStyles: Record<string, { background: string; color: string; border: string }> = {
  synced: { background: '#ECFDF3', color: '#2D7A4F', border: '#A7DFC0' },
  syncing: { background: '#EEF2FF', color: '#4338CA', border: '#C7D2FE' },
  pending: { background: '#F8F8F7', color: '#6B6760', border: '#E5E5E3' },
  error: { background: '#FEF6F5', color: '#C0392B', border: '#F5BDB8' },
  active: { background: '#ECFDF3', color: '#2D7A4F', border: '#A7DFC0' },
  expired: { background: '#FEF6F5', color: '#C0392B', border: '#F5BDB8' },
  paused: { background: '#F8F8F7', color: '#6B6760', border: '#E5E5E3' },
};

function SyncBadge({ status }: { status: string | null }) {
  const s = syncStatusStyles[status || 'pending'] || syncStatusStyles['pending'];
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 500,
        padding: '2px 8px',
        borderRadius: 6,
        background: s.background,
        color: s.color,
        border: `1px solid ${s.border}`,
        whiteSpace: 'nowrap',
      }}
    >
      {status || 'pending'}
    </span>
  );
}

function DemoBadge() {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        fontSize: 11,
        fontWeight: 500,
        padding: '2px 7px',
        borderRadius: 6,
        background: '#F5F4F2',
        color: '#7A7570',
        border: '1px solid #E5E5E3',
      }}
      data-testid="demo-badge"
    >
      <FlaskConical style={{ width: 10, height: 10 }} />
      Demo
    </span>
  );
}

function isDemo(conn: ConnectionData): boolean {
  return conn.fbUserId === "demo";
}

export default function ConnectionsContent() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const search = useSearch();
  const params = new URLSearchParams(search);

  useEffect(() => {
    if (params.get("connected") === "true") {
      toast({ title: "Connected!", description: "Your Meta ad account has been connected successfully." });
    }
    if (params.get("error")) {
      const err = params.get("error");
      if (err === "not_configured") {
        toast({ title: "Not configured", description: "Meta App credentials are not set. Please contact your administrator.", variant: "destructive" });
      } else {
        toast({ title: "Connection failed", description: err || "An error occurred", variant: "destructive" });
      }
    }
  }, []);

  const authUser = useAuthUser();
  const isAdmin = !!authUser?.isAdmin;

  const { data: connections = [], isLoading } = useQuery<ConnectionData[]>({
    queryKey: ["/api/meta/connections"],
  });

  const { data: brandsRaw = [], isError: brandsError } = useQuery<Brand[]>({
    queryKey: ["/api/brands"],
    queryFn: async () => {
      const res = await fetch("/api/brands", { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      return res.json();
    },
    staleTime: 0,
    refetchOnMount: true,
  });
  const brands: Brand[] = Array.isArray(brandsRaw) ? brandsRaw.filter(b => b && !isNaN(Number(b.id)) && typeof b.name === "string") : [];

  const { data: configStatus, isLoading: configLoading } = useQuery({
    queryKey: ["/api/meta/config/status"],
    queryFn: async () => {
      const res = await fetch("/api/meta/config/status");
      if (!res.ok) throw new Error("Failed to check config status");
      return res.json();
    },
  });

  const { data: demoStatus } = useQuery<{
    exists: boolean;
    extendedData?: boolean;
    counts?: { insights: number; geoRows: number; hourlyRows: number; creativeBreakdownRows: number } | null;
  }>({
    queryKey: ["/api/meta/demo/status"],
    queryFn: async () => {
      const res = await fetch("/api/meta/demo/status");
      if (!res.ok) throw new Error("Failed to check demo status");
      return res.json();
    },
    enabled: connections.some(c => c.fbUserId === "demo"),
  });

  const { data: platformDemoStatus } = useQuery<{ meta: { exists: boolean }; tiktok: { exists: boolean } }>({
    queryKey: ["/api/demo/status"],
    queryFn: async () => {
      const res = await fetch("/api/demo/status");
      if (!res.ok) throw new Error("Failed to check platform demo status");
      return res.json();
    },
  });

  interface UnifiedAccount {
    id: number;
    accountName: string;
    externalAccountId: string;
    status: string;
    isDemo: boolean;
    platformSlug: string;
    platformName: string;
    platformColor: string;
  }

  const { data: unifiedAccounts = [] } = useQuery<UnifiedAccount[]>({
    queryKey: ["/api/unified-accounts"],
    queryFn: async () => {
      const res = await fetch("/api/unified-accounts");
      if (!res.ok) throw new Error("Failed to fetch unified accounts");
      return res.json();
    },
  });

  const tiktokDemoAccounts = unifiedAccounts.filter(a => a.isDemo && a.platformSlug === "tiktok");

  const removeTiktokDemoMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/demo/tiktok", { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to remove TikTok demo data");
    },
    onSuccess: () => {
      invalidateAllAccountData();
      toast({ title: "TikTok demo removed", description: "All TikTok demo accounts and data have been deleted." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to remove TikTok demo data.", variant: "destructive" });
    },
  });

  const invalidateAllAccountData = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/meta/connections"] });
    queryClient.invalidateQueries({ queryKey: ["/api/meta/accounts"] });
    queryClient.invalidateQueries({ queryKey: ["/api/meta/demo/status"] });
    queryClient.invalidateQueries({ queryKey: ["/api/demo/status"] });
    queryClient.invalidateQueries({ queryKey: ["/api/unified-accounts"] });
    queryClient.invalidateQueries({ queryKey: ["/api/platforms"] });
    queryClient.invalidateQueries({ queryKey: ["/api/dashboard"] });
    queryClient.invalidateQueries({ queryKey: ["/api/campaigns"] });
    queryClient.invalidateQueries({ queryKey: ["/api/insights"] });
    queryClient.invalidateQueries({ queryKey: ["/api/unified-accounts"] });
    queryClient.invalidateQueries({ queryKey: ["/api/ad-accounts"] });
    queryClient.invalidateQueries({ queryKey: ["/api/ad-accounts/brand-links"] });
    queryClient.invalidateQueries({ predicate: (query) => {
      const key = query.queryKey[0];
      return typeof key === "string" && key.includes("/api/platforms/brands");
    }});
  };

  const disconnectMutation = useMutation({
    mutationFn: async (connectionId: number) => {
      const res = await fetch(`/api/meta/oauth/disconnect/${connectionId}`, { method: "POST" });
      if (!res.ok) throw new Error("Disconnect failed");
    },
    onSuccess: () => {
      invalidateAllAccountData();
      toast({ title: "Disconnected", description: "Meta account and all associated data have been removed." });
    },
  });

  const refreshMutation = useMutation({
    mutationFn: async (connectionId: number) => {
      const res = await fetch(`/api/meta/oauth/refresh/${connectionId}`, { method: "POST" });
      if (!res.ok) throw new Error("Refresh failed");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meta/connections"] });
      toast({ title: "Token refreshed", description: "Your access token has been refreshed." });
    },
  });

  const updateAccountMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const res = await fetch(`/api/meta/accounts/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!res.ok) throw new Error("Update failed");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meta/connections"] });
      queryClient.invalidateQueries({ queryKey: ["/api/meta/accounts"] });
    },
  });

  const syncMutation = useMutation({
    mutationFn: async (accountId: number) => {
      const res = await fetch(`/api/meta/accounts/${accountId}/sync`, { method: "POST" });
      if (!res.ok) throw new Error("Sync failed");
    },
    onSuccess: () => {
      toast({ title: "Sync started", description: "Data sync has been triggered." });
    },
  });

  const seedDemoMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/meta/seed-demo", { method: "POST" });
      if (!res.ok) throw new Error("Failed to seed demo data");
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/meta/connections"] });
      queryClient.invalidateQueries({ queryKey: ["/api/meta/accounts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/meta/demo/status"] });
      if (data.alreadyExists) {
        toast({ title: "Demo data already loaded", description: "Demo accounts are already available in your connections." });
      } else {
        const extras = [
          data.geoRows && `${data.geoRows} geo`,
          data.hourlyRows && `${data.hourlyRows} hourly`,
          data.creativeBreakdownRows && `${data.creativeBreakdownRows} carousel`,
        ].filter(Boolean).join(", ");
        toast({
          title: "Demo data loaded",
          description: `Created ${data.accounts} accounts, ${data.campaigns} campaigns, ${data.insights} insights${extras ? `, ${extras} breakdown rows` : ""}.`,
        });
      }
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to load demo data.", variant: "destructive" });
    },
  });

  const removeDemoMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/meta/demo", { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to remove demo data");
    },
    onSuccess: () => {
      invalidateAllAccountData();
      toast({ title: "Demo data removed", description: "All demo accounts and data have been deleted." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to remove demo data.", variant: "destructive" });
    },
  });

  const purgeAccountMutation = useMutation({
    mutationFn: async (accountId: number) => {
      const res = await fetch(`/api/platforms/ad-accounts/${accountId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to purge account data");
    },
    onSuccess: () => {
      invalidateAllAccountData();
      toast({ title: "Account data purged", description: "All data for this account has been permanently deleted." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to purge account data.", variant: "destructive" });
    },
  });

  const handleConnect = () => {
    if (configLoading) return;
    if (!configStatus?.configured) {
      toast({
        title: "Meta App not configured",
        description: "META_APP_ID and META_APP_SECRET environment variables must be set by the platform administrator.",
        variant: "destructive",
      });
      return;
    }
    window.location.href = "/api/meta/oauth/connect";
  };

  const anyExpiringToken = connections.some(c => {
    const days = daysUntilExpiry(c.tokenExpiresAt);
    return days !== null && days <= 7 && c.status === "active" && !isDemo(c);
  });

  const hasDemoConnection = connections.some(c => isDemo(c));

  if (isLoading) {
    return (
      <div data-testid="connections-loading">
        <div style={{ height: 32, background: '#EDEAE5', borderRadius: 8, width: 192, marginBottom: 24, animation: 'pulse 1.5s ease-in-out infinite' }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {[1, 2].map(i => (
            <div key={i} style={{ height: 192, background: '#F5F3EE', borderRadius: 12, animation: 'pulse 1.5s ease-in-out infinite' }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="connections-content">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, color: '#1A1A1A', margin: 0 }}>Meta Ad Accounts</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Demo data is an admin-only tool — never shown to regular users */}
          {isAdmin && !hasDemoConnection && (
            <button
              data-testid="load-demo-button"
              onClick={() => seedDemoMutation.mutate()}
              disabled={seedDemoMutation.isPending}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 14px',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 500,
                color: '#6B6760',
                background: 'transparent',
                border: '1px solid #E5E5E3',
                cursor: seedDemoMutation.isPending ? 'not-allowed' : 'pointer',
                opacity: seedDemoMutation.isPending ? 0.6 : 1,
                transition: 'background 120ms',
              }}
              onMouseEnter={(e) => { if (!seedDemoMutation.isPending) (e.currentTarget as HTMLButtonElement).style.background = '#F5F3EE'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
            >
              {seedDemoMutation.isPending ? (
                <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />
              ) : (
                <FlaskConical style={{ width: 14, height: 14 }} />
              )}
              {seedDemoMutation.isPending ? "Loading Demo..." : "Load Demo Data"}
            </button>
          )}
          <button
            data-testid="connect-meta-button"
            onClick={handleConnect}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 14px',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 500,
              color: '#6366F1',
              background: 'transparent',
              border: '1px solid #6366F1',
              cursor: 'pointer',
              transition: 'background 120ms',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F0F0FE'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
          >
            <Plus style={{ width: 14, height: 14 }} />
            Connect Meta Account
          </button>
        </div>
      </div>

      {anyExpiringToken && (
        <div
          style={{
            background: '#FFFBEB',
            border: '1px solid #F5E0A0',
            borderRadius: 10,
            padding: '12px 16px',
            marginBottom: 24,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
          data-testid="token-warning"
        >
          <AlertTriangle style={{ width: 18, height: 18, color: '#B45309', flexShrink: 0 }} />
          <p style={{ fontSize: 13, color: '#92400E', margin: 0 }}>One or more tokens are expiring within 7 days. Refresh them to avoid sync interruptions.</p>
        </div>
      )}

      {connections.length === 0 ? (
        <div
          style={{
            padding: '48px 24px',
            background: '#F8F8F7',
            border: '1px solid #E5E5E3',
            borderRadius: 12,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
          data-testid="empty-state"
        >
          <Link2 style={{ width: 40, height: 40, color: '#C4BFB9', marginBottom: 16 }} />
          <h2 style={{ fontSize: 16, fontWeight: 600, color: '#1A1A1A', margin: '0 0 8px' }}>No Meta accounts connected</h2>
          <p style={{ fontSize: 13.5, color: '#8C8578', margin: '0 0 24px', maxWidth: 360 }}>Connect your Meta ad account to start syncing campaigns, ad sets, and performance data.</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={handleConnect}
              data-testid="connect-meta-empty-button"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '7px 16px',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 500,
                color: '#6366F1',
                background: 'transparent',
                border: '1px solid #6366F1',
                cursor: 'pointer',
                transition: 'background 120ms',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F0F0FE'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
            >
              <ExternalLink style={{ width: 14, height: 14 }} />
              Connect Meta Account
            </button>
            {isAdmin && <button
              onClick={() => seedDemoMutation.mutate()}
              disabled={seedDemoMutation.isPending}
              data-testid="load-demo-empty-button"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '7px 16px',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 500,
                color: '#6B6760',
                background: 'transparent',
                border: '1px solid #E5E5E3',
                cursor: seedDemoMutation.isPending ? 'not-allowed' : 'pointer',
                opacity: seedDemoMutation.isPending ? 0.6 : 1,
                transition: 'background 120ms',
              }}
              onMouseEnter={(e) => { if (!seedDemoMutation.isPending) (e.currentTarget as HTMLButtonElement).style.background = '#F0EDE9'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
            >
              {seedDemoMutation.isPending ? (
                <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />
              ) : (
                <FlaskConical style={{ width: 14, height: 14 }} />
              )}
              {seedDemoMutation.isPending ? "Loading..." : "Load Demo Data"}
            </button>}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {connections.map(conn => {
            const isDemoConn = isDemo(conn);
            const expiryDays = daysUntilExpiry(conn.tokenExpiresAt);

            const statusDotColor = isDemoConn
              ? '#A09890'
              : conn.status === "active" ? '#4CAF88' : conn.status === "expired" ? '#C0392B' : '#B5B0A8';

            return (
              <div
                key={conn.id}
                style={{
                  border: '1px solid #E5E5E3',
                  borderRadius: 12,
                  background: '#FAFAF8',
                  overflow: 'hidden',
                }}
                data-testid={`connection-card-${conn.id}`}
              >
                <div style={{ padding: '16px 20px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: statusDotColor, display: 'inline-block', marginTop: 4, flexShrink: 0 }} />
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                          <p style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A', margin: 0 }}>{conn.fbUserName || "Unknown User"}</p>
                          {isDemoConn && <DemoBadge />}
                        </div>
                        {!isDemoConn && (
                          <p style={{ fontSize: 12.5, color: '#8C8578', margin: 0 }}>
                            {conn.fbUserEmail}
                            {conn.createdAt && ` · Connected ${new Date(conn.createdAt).toLocaleDateString()}`}
                            {expiryDays !== null && ` · Token expires in ${expiryDays} days`}
                          </p>
                        )}
                        {isDemoConn && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <p style={{ fontSize: 12.5, color: '#8C8578', margin: 0 }}>
                                Sample data for testing · Not connected to Meta
                              </p>
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 3,
                                  fontSize: 11,
                                  fontWeight: 500,
                                  padding: '2px 7px',
                                  borderRadius: 6,
                                  background: '#EEF2FF',
                                  color: '#4338CA',
                                  border: '1px solid #C7D2FE',
                                }}
                                data-testid="extended-data-badge"
                              >
                                <Database style={{ width: 10, height: 10 }} />
                                Extended Data
                              </span>
                            </div>
                            {demoStatus?.counts && (
                              <p style={{ fontSize: 11.5, color: '#A09890', margin: 0 }} data-testid="demo-row-counts">
                                {demoStatus.counts.insights.toLocaleString()} insights · {demoStatus.counts.geoRows.toLocaleString()} geo · {demoStatus.counts.hourlyRows.toLocaleString()} hourly · {demoStatus.counts.creativeBreakdownRows.toLocaleString()} carousel
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {isDemoConn ? (
                        <button
                          onClick={() => {
                            if (confirm("Remove all demo data? This will delete the demo connection and all associated accounts, campaigns, and insights.")) {
                              removeDemoMutation.mutate();
                            }
                          }}
                          disabled={removeDemoMutation.isPending}
                          data-testid="remove-demo-button"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '4px 10px',
                            borderRadius: 7,
                            fontSize: 12.5,
                            fontWeight: 500,
                            color: '#8C8578',
                            background: 'transparent',
                            border: '1px solid #E5E5E3',
                            cursor: removeDemoMutation.isPending ? 'not-allowed' : 'pointer',
                            opacity: removeDemoMutation.isPending ? 0.6 : 1,
                            transition: 'color 120ms, border-color 120ms, background 120ms',
                          }}
                          onMouseEnter={(e) => {
                            const btn = e.currentTarget as HTMLButtonElement;
                            btn.style.color = '#C0392B';
                            btn.style.borderColor = '#F5BDB8';
                            btn.style.background = '#FEF6F5';
                          }}
                          onMouseLeave={(e) => {
                            const btn = e.currentTarget as HTMLButtonElement;
                            btn.style.color = '#8C8578';
                            btn.style.borderColor = '#E5E5E3';
                            btn.style.background = 'transparent';
                          }}
                        >
                          {removeDemoMutation.isPending ? (
                            <Loader2 style={{ width: 12, height: 12, animation: 'spin 1s linear infinite' }} />
                          ) : (
                            <Trash2 style={{ width: 12, height: 12 }} />
                          )}
                          Remove Demo
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => refreshMutation.mutate(conn.id)}
                            disabled={refreshMutation.isPending}
                            data-testid={`refresh-connection-${conn.id}`}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              padding: '4px 10px',
                              borderRadius: 7,
                              fontSize: 12.5,
                              fontWeight: 500,
                              color: '#6B6760',
                              background: 'transparent',
                              border: '1px solid #E5E5E3',
                              cursor: refreshMutation.isPending ? 'not-allowed' : 'pointer',
                              opacity: refreshMutation.isPending ? 0.6 : 1,
                              transition: 'background 120ms',
                            }}
                            onMouseEnter={(e) => { if (!refreshMutation.isPending) (e.currentTarget as HTMLButtonElement).style.background = '#F5F3EE'; }}
                            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                          >
                            <RefreshCw style={{ width: 12, height: 12, ...(refreshMutation.isPending ? { animation: 'spin 1s linear infinite' } : {}) }} />
                            Refresh
                          </button>
                          <button
                            onClick={() => {
                              if (confirm("Disconnect this Meta account? This will permanently delete ALL data for this connection, including campaigns, ad sets, ads, and performance data. This cannot be undone.")) {
                                disconnectMutation.mutate(conn.id);
                              }
                            }}
                            data-testid={`disconnect-connection-${conn.id}`}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 30,
                              height: 30,
                              borderRadius: 7,
                              color: '#8C8578',
                              background: 'transparent',
                              border: '1px solid #E5E5E3',
                              cursor: 'pointer',
                              transition: 'color 120ms, border-color 120ms, background 120ms',
                            }}
                            onMouseEnter={(e) => {
                              const btn = e.currentTarget as HTMLButtonElement;
                              btn.style.color = '#C0392B';
                              btn.style.borderColor = '#F5BDB8';
                              btn.style.background = '#FEF6F5';
                            }}
                            onMouseLeave={(e) => {
                              const btn = e.currentTarget as HTMLButtonElement;
                              btn.style.color = '#8C8578';
                              btn.style.borderColor = '#E5E5E3';
                              btn.style.background = 'transparent';
                            }}
                          >
                            <X style={{ width: 12, height: 12 }} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  <div style={{ border: '1px solid #EDEAE5', borderRadius: 8, overflow: 'hidden' }}>
                    {conn.accounts.map((account, idx) => (
                      <div
                        key={account.id}
                        style={{
                          padding: '10px 14px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          borderTop: idx > 0 ? '1px solid #EDEAE5' : undefined,
                          background: '#FFFFFF',
                        }}
                        data-testid={`account-row-${account.id}`}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: 1 }}>
                          <Switch
                            checked={account.isActive || false}
                            onCheckedChange={(checked) =>
                              updateAccountMutation.mutate({ id: account.id, data: { isActive: checked } })
                            }
                            data-testid={`account-toggle-${account.id}`}
                          />
                          <div style={{ minWidth: 0, maxWidth: 220 }}>
                            <p style={{ fontSize: 13, fontWeight: 500, color: '#1A1A1A', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{account.accountName}</p>
                            <p style={{ fontSize: 11.5, color: '#A09890', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{account.accountId}</p>
                          </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, flexWrap: 'nowrap' }}>
                          {!isDemoConn && (
                            brandsError ? (
                              <span
                                style={{ fontSize: 11.5, color: '#C0392B', background: '#FEF6F5', border: '1px solid #F5BDB8', borderRadius: 6, padding: '3px 8px', whiteSpace: 'nowrap' }}
                                data-testid={`link-brand-error-${account.id}`}
                                title="Could not load brands. Refresh the page to try again."
                              >
                                Brand load failed
                              </span>
                            ) : (
                            <Select
                              value={account.brandId?.toString() || "none"}
                              onValueChange={(val) =>
                                updateAccountMutation.mutate({
                                  id: account.id,
                                  data: { brandId: val === "none" ? null : parseInt(val) },
                                })
                              }
                            >
                              <SelectTrigger className="w-[140px] h-8 text-xs" data-testid={`link-brand-${account.id}`}>
                                <SelectValue placeholder="Link brand..." />
                              </SelectTrigger>
                              {/* z-[10001]: the settings modal overlay sits at zIndex
                                  10000; SelectContent's default z-50 opened the dropdown
                                  BEHIND the modal — invisible, "doesn't work". */}
                              <SelectContent className="z-[10001]">
                                <SelectItem value="none">No brand linked</SelectItem>
                                {brands.map(b => (
                                  <SelectItem key={b.id} value={b.id.toString()}>{b.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            )
                          )}

                          <SyncBadge status={account.syncStatus} />

                          <span style={{ fontSize: 11.5, color: '#B5B0A8', whiteSpace: 'nowrap' }}>
                            {formatTimeAgo(account.lastSyncAt)}
                          </span>

                          {account.unifiedAccountId && (
                            <button
                              onClick={() => {
                                if (confirm(`Purge all data for "${account.accountName}"? This will permanently delete all campaigns, ad sets, ads, and performance data for this account. This cannot be undone.`)) {
                                  purgeAccountMutation.mutate(account.unifiedAccountId!);
                                }
                              }}
                              disabled={purgeAccountMutation.isPending}
                              data-testid={`purge-account-${account.id}`}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                padding: '3px 8px',
                                height: 26,
                                borderRadius: 6,
                                fontSize: 11.5,
                                fontWeight: 500,
                                color: '#8C8578',
                                background: 'transparent',
                                border: '1px solid #E5E5E3',
                                cursor: purgeAccountMutation.isPending ? 'not-allowed' : 'pointer',
                                opacity: purgeAccountMutation.isPending ? 0.6 : 1,
                                transition: 'color 120ms, border-color 120ms, background 120ms',
                              }}
                              onMouseEnter={(e) => {
                                const btn = e.currentTarget as HTMLButtonElement;
                                btn.style.color = '#C0392B';
                                btn.style.borderColor = '#F5BDB8';
                                btn.style.background = '#FEF6F5';
                              }}
                              onMouseLeave={(e) => {
                                const btn = e.currentTarget as HTMLButtonElement;
                                btn.style.color = '#8C8578';
                                btn.style.borderColor = '#E5E5E3';
                                btn.style.background = 'transparent';
                              }}
                            >
                              {purgeAccountMutation.isPending ? (
                                <Loader2 style={{ width: 11, height: 11, animation: 'spin 1s linear infinite' }} />
                              ) : (
                                <Eraser style={{ width: 11, height: 11 }} />
                              )}
                              Purge Data
                            </button>
                          )}
                          {!isDemoConn && (
                            <button
                              onClick={() => syncMutation.mutate(account.id)}
                              disabled={syncMutation.isPending}
                              data-testid={`sync-account-${account.id}`}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 26,
                                height: 26,
                                borderRadius: 6,
                                color: '#8C8578',
                                background: 'transparent',
                                border: 'none',
                                cursor: syncMutation.isPending ? 'not-allowed' : 'pointer',
                                opacity: syncMutation.isPending ? 0.6 : 1,
                                transition: 'background 120ms',
                              }}
                              onMouseEnter={(e) => { if (!syncMutation.isPending) (e.currentTarget as HTMLButtonElement).style.background = '#F0EDE9'; }}
                              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                            >
                              <RefreshCw style={{ width: 12, height: 12, ...(syncMutation.isPending ? { animation: 'spin 1s linear infinite' } : {}) }} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tiktokDemoAccounts.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <h2 style={{ fontSize: 15, fontWeight: 600, color: '#1A1A1A', margin: 0 }}>TikTok Demo Accounts</h2>
              <DemoBadge />
            </div>
            <button
              onClick={() => {
                if (confirm("Remove all TikTok demo data? This will delete all TikTok demo accounts, campaigns, ads, and performance data. This cannot be undone.")) {
                  removeTiktokDemoMutation.mutate();
                }
              }}
              disabled={removeTiktokDemoMutation.isPending}
              data-testid="remove-tiktok-demo-button"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '4px 10px',
                borderRadius: 7,
                fontSize: 12.5,
                fontWeight: 500,
                color: '#8C8578',
                background: 'transparent',
                border: '1px solid #E5E5E3',
                cursor: removeTiktokDemoMutation.isPending ? 'not-allowed' : 'pointer',
                opacity: removeTiktokDemoMutation.isPending ? 0.6 : 1,
                transition: 'color 120ms, border-color 120ms, background 120ms',
              }}
              onMouseEnter={(e) => {
                const btn = e.currentTarget as HTMLButtonElement;
                btn.style.color = '#C0392B';
                btn.style.borderColor = '#F5BDB8';
                btn.style.background = '#FEF6F5';
              }}
              onMouseLeave={(e) => {
                const btn = e.currentTarget as HTMLButtonElement;
                btn.style.color = '#8C8578';
                btn.style.borderColor = '#E5E5E3';
                btn.style.background = 'transparent';
              }}
            >
              {removeTiktokDemoMutation.isPending ? (
                <Loader2 style={{ width: 12, height: 12, animation: 'spin 1s linear infinite' }} />
              ) : (
                <Trash2 style={{ width: 12, height: 12 }} />
              )}
              Remove TikTok Demo
            </button>
          </div>
          <div
            style={{
              border: '1px solid #E5E5E3',
              borderRadius: 12,
              background: '#FAFAF8',
              overflow: 'hidden',
            }}
            data-testid="tiktok-demo-card"
          >
            <div style={{ padding: '16px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#A09890', display: 'inline-block', flexShrink: 0 }} />
                <p style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A', margin: 0 }}>TikTok Demo</p>
                <p style={{ fontSize: 12.5, color: '#8C8578', margin: 0 }}>Sample data for testing · Not connected to TikTok</p>
              </div>
              <div style={{ border: '1px solid #EDEAE5', borderRadius: 8, overflow: 'hidden' }}>
                {tiktokDemoAccounts.map((account, idx) => (
                  <div
                    key={account.id}
                    style={{
                      padding: '10px 14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderTop: idx > 0 ? '1px solid #EDEAE5' : undefined,
                      background: '#FFFFFF',
                    }}
                    data-testid={`tiktok-demo-account-${account.id}`}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: 1 }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: account.platformColor, flexShrink: 0 }}>
                        <Music2 style={{ width: 14, height: 14, color: '#FFFFFF' }} />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <p style={{ fontSize: 13, fontWeight: 500, color: '#1A1A1A', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{account.accountName}</p>
                        <p style={{ fontSize: 11.5, color: '#A09890', margin: 0 }}>{account.externalAccountId}</p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 500,
                          padding: '2px 8px',
                          borderRadius: 6,
                          background: 'rgba(238,29,82,0.08)',
                          color: '#EE1D52',
                          border: '1px solid rgba(238,29,82,0.25)',
                        }}
                      >
                        TikTok
                      </span>
                      <SyncBadge status={account.status} />
                      <button
                        onClick={() => {
                          if (confirm(`Purge all data for "${account.accountName}"? This will permanently delete all campaigns, ad groups, ads, and performance data for this account. This cannot be undone.`)) {
                            purgeAccountMutation.mutate(account.id);
                          }
                        }}
                        disabled={purgeAccountMutation.isPending}
                        data-testid={`purge-tiktok-account-${account.id}`}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          padding: '3px 8px',
                          height: 26,
                          borderRadius: 6,
                          fontSize: 11.5,
                          fontWeight: 500,
                          color: '#8C8578',
                          background: 'transparent',
                          border: '1px solid #E5E5E3',
                          cursor: purgeAccountMutation.isPending ? 'not-allowed' : 'pointer',
                          opacity: purgeAccountMutation.isPending ? 0.6 : 1,
                          transition: 'color 120ms, border-color 120ms, background 120ms',
                        }}
                        onMouseEnter={(e) => {
                          const btn = e.currentTarget as HTMLButtonElement;
                          btn.style.color = '#C0392B';
                          btn.style.borderColor = '#F5BDB8';
                          btn.style.background = '#FEF6F5';
                        }}
                        onMouseLeave={(e) => {
                          const btn = e.currentTarget as HTMLButtonElement;
                          btn.style.color = '#8C8578';
                          btn.style.borderColor = '#E5E5E3';
                          btn.style.background = 'transparent';
                        }}
                      >
                        {purgeAccountMutation.isPending ? (
                          <Loader2 style={{ width: 11, height: 11, animation: 'spin 1s linear infinite' }} />
                        ) : (
                          <Eraser style={{ width: 11, height: 11 }} />
                        )}
                        Purge Data
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
